import { NgModule } from '@angular/core';
import { Route, RouterModule } from '@angular/router';
import { IpoClientFeaturesTogglerComponent } from '@ip-only/client-features';
import { AppAuthGuard } from './app-auth-guard';
import {
  CHANGE_ORDER_REQUEST_APP_COUNTRIES,
  CONSTRUCTION_DOCUMENTS_APP_COUNTRIES,
  DELIVERY_OVERVIEW_APP_COUNTRIES,
  HOME_APP_COUNTRIES,
  INSTALLATION_REPORTING_APP_COUNTRIES,
  LAND_AGREEMENTS_APP_COUNTRIES,
  LAND_AGREEMENTS_APP_COUNTRIES_V2,
  LAND_SURVEYS_APP_COUNTRIES,
  NETWORK_IMPORT_APP_COUNTRIES,
  ROLLOUT_PROGRESS_APP_COUNTRIES,
  SPAN_PROTECTION_APP_COUNTRIES,
  USERS_APP_COUNTRIES,
} from './apps-deploy-countries-def/apps-countries';
import { ChangelogComponent } from './changelog/changelog.component';
import { FinlandRouteGuard } from './finland-routing-guard';
import { GuideDocumentComponent } from './guide-document/guide-document.component';
import { HomeComponent } from './home/home.component';
import { AppLayoutComponent } from './layout/app-layout.component';
import { MissingRolesComponent } from './missing-roles/missing-roles.component';
import { NoAccessComponent } from './shared/no-access/no-access.component';
import {
  CONSTRUCTION_DOCUMENTS_ROLES,
  CONTRACTOR_USER_ADMIN_ROLES,
  DELIVERY_OVERVIEW_ROLES,
  DELIVERY_PORTAL_BETA_USER,
  LAND_AGREEMENT_ROLES,
  LAND_SURVEY_ROLES,
  NETWORK_IMPORT_ROLES,
  SPAN_PROTECTION_ROLES,
  SPAN_ROLES_VIEW,
  SYSTEM_DEVELOPER,
  WORK_VARIATION_ROLES,
} from './user-roles-def/user-roles';
import { RolesCondition } from './user/user';
import { VideoInstructionComponent } from './video-instruction/video-instruction.component';
export interface AuthGuardData {
  requiredRoles: string[];
  rolesCondition: RolesCondition;
}
interface RouteData extends Partial<AuthGuardData> {
  requiredFeatureFlag?: string;
}

type ContractorPortalRoutes = {
  data?: RouteData;
} & Route;

const routes: ContractorPortalRoutes[] = [
  {
    path: 'client-features',
    component: IpoClientFeaturesTogglerComponent,
    data: {
      requiredRoles: [DELIVERY_PORTAL_BETA_USER, SYSTEM_DEVELOPER],
      rolesCondition: 'or',
      enableCountries: HOME_APP_COUNTRIES,
    },
  },
  {
    path: '',
    component: AppLayoutComponent,
    children: [
      {
        path: 'home',
        loadChildren: () =>
          import('./home/home-app.module').then((m) => m.HomeAppModule),
        component: HomeComponent,
        canActivate: [AppAuthGuard],
        data: {
          enableCountries: HOME_APP_COUNTRIES,
        },
      },
      {
        path: 'users',
        loadChildren: () =>
          import('./contractor-users/contractor-users.module').then(
            (m) => m.ContractorUsersModule,
          ),
        canActivate: [AppAuthGuard],
        data: {
          requiredRoles: CONTRACTOR_USER_ADMIN_ROLES,
          rolesCondition: 'or',
          enableCountries: USERS_APP_COUNTRIES,
        },
      },
      {
        path: 'land-agreement',
        loadChildren: () =>
          import('./land-agreement-app/land-agreement.module').then(
            (m) => m.LandAgreementModule,
          ),
        canActivate: [AppAuthGuard],
        data: {
          requiredRoles: LAND_AGREEMENT_ROLES,
          rolesCondition: 'or',
          enableCountries: LAND_AGREEMENTS_APP_COUNTRIES,
        },
      },
      {
        path: 'land-agreement-v2',
        loadChildren: () =>
          import('./land-agreement-v2-app/land-agreement-v2.routes').then(
            (m) => m.LandAgreementV2Routes,
          ),
        data: {
          requiredRoles: LAND_AGREEMENT_ROLES,
          rolesCondition: 'or',
          enableCountries: LAND_AGREEMENTS_APP_COUNTRIES_V2,
          rolesConditionFI: [DELIVERY_PORTAL_BETA_USER],
        },
        canActivate: [AppAuthGuard],
      },

      {
        path: 'land-survey',
        loadChildren: () =>
          import('./land-survey-app/land-survey.module').then(
            (m) => m.LandSurveyModule,
          ),
        canActivate: [AppAuthGuard, FinlandRouteGuard],
        data: {
          requiredRoles: LAND_SURVEY_ROLES,
          rolesCondition: 'or',
          enableCountries: LAND_SURVEYS_APP_COUNTRIES,
        },
      },
      {
        path: 'work-variation',
        loadChildren: () =>
          import('./work-variation-app/work-variation-app.module').then(
            (m) => m.WorkVariationAppModule,
          ),
        canActivate: [AppAuthGuard],
        data: {
          requiredRoles: WORK_VARIATION_ROLES,
          rolesCondition: 'or',
          enableCountries: CHANGE_ORDER_REQUEST_APP_COUNTRIES,
        },
      },
      {
        path: 'rollout-progress',
        loadChildren: () =>
          import('./span-progress-app/span-progress.module').then(
            (m) => m.SpanProgressModule,
          ),
        canActivate: [AppAuthGuard],
        data: {
          requiredRoles: SPAN_ROLES_VIEW,
          rolesCondition: 'or',
          enableCountries: ROLLOUT_PROGRESS_APP_COUNTRIES,
        },
      },
      {
        path: 'network-import',
        loadChildren: () =>
          import('./network-import-app/network-import-app.module').then(
            (m) => m.NetworkImportAppModule,
          ),
        canActivate: [AppAuthGuard],
        data: {
          requiredRoles: NETWORK_IMPORT_ROLES,
          rolesCondition: 'or',
          requiredFeatureFlag: 'networkImport',
          enableCountries: NETWORK_IMPORT_APP_COUNTRIES,
          rolesConditionFI: [DELIVERY_PORTAL_BETA_USER],
        },
      },
      {
        path: 'installation-reporting',
        loadChildren: () =>
          import('./installation-reporting-app/installation-reporting-app.module').then(
            (m) => m.InstallationReportingAppModule,
          ),
        data: {
          requiredRoles: SPAN_ROLES_VIEW,
          rolesCondition: 'or',
          enableCountries: INSTALLATION_REPORTING_APP_COUNTRIES,
        },
        canActivate: [AppAuthGuard],
      },
      {
        path: 'delivery-overview',
        loadChildren: () =>
          import('./delivery-overview-app/delivery-overview-app.module').then(
            (m) => m.DeliveryOverviewAppModule,
          ),
        data: {
          requiredRoles: DELIVERY_OVERVIEW_ROLES,
          rolesCondition: 'or',
          enableCountries: DELIVERY_OVERVIEW_APP_COUNTRIES,
        },

        canActivate: [AppAuthGuard],
      },
      {
        path: 'construction-documents',
        loadChildren: () =>
          import('./construction-documents-app/construction-documents-app.module').then(
            (m) => m.ConstructionDocumentsAppModule,
          ),
        data: {
          requiredRoles: CONSTRUCTION_DOCUMENTS_ROLES,
          rolesCondition: 'or',
          enableCountries: CONSTRUCTION_DOCUMENTS_APP_COUNTRIES,
        },

        canActivate: [AppAuthGuard],
      },
      {
        path: 'span-protection',
        loadChildren: () =>
          import('./span-protection-app/span-protection.routes').then(
            (m) => m.SpanProtectionRoutes,
          ),
        data: {
          requiredRoles: SPAN_PROTECTION_ROLES,
          rolesCondition: 'or',
          enableCountries: SPAN_PROTECTION_APP_COUNTRIES,
          requiredFeatureFlag: 'spanProtection',
        },
        canActivate: [AppAuthGuard],
      },
      {
        path: 'changelog',
        component: ChangelogComponent,
        canActivate: [AppAuthGuard],
        data: {
          enableCountries: HOME_APP_COUNTRIES,
        },
      },
      {
        path: 'guide-document',
        component: GuideDocumentComponent,
        canActivate: [AppAuthGuard],
        data: {
          enableCountries: HOME_APP_COUNTRIES,
        },
      },
      {
        path: 'video-instruction',
        component: VideoInstructionComponent,
        canActivate: [AppAuthGuard],
        data: {
          enableCountries: HOME_APP_COUNTRIES,
        },
      },
      {
        path: 'missing-roles',
        component: MissingRolesComponent,
        canActivate: [AppAuthGuard],
        data: {
          enableCountries: HOME_APP_COUNTRIES,
        },
      },
      {
        path: 'no-access',
        component: NoAccessComponent,
        canActivate: [AppAuthGuard],
      },
      { path: 'span', redirectTo: 'rollout-progress' },
      { path: '**', redirectTo: '/home' },
    ],
  },

  { path: '**', redirectTo: '' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      useHash: false,
      bindToComponentInputs: true,
      paramsInheritanceStrategy: 'always',
      scrollPositionRestoration: 'top',
      anchorScrolling: 'enabled',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
